import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="form-picker"
//
// The single behaviour engine behind every checkbox / select-all / bulk-action
// list (decision-45). Counts selected (enabled) checkboxes, writes the live
// "N selected" label, shows the contextual selection bar while anything is
// selected, and enables the bulk action button(s). Disabled checkboxes
// (rows that cannot be acted on — already attached, say) never count.
//
// `AppUi::SelectableList::Component` renders the canonical markup for this.
// Hand-rolled clusters still work: every target is optional and plural, so a
// list with only checkbox + count + submit behaves exactly as it did before
// the bar and clear action existed.
export default class extends Controller {
  static targets = ["checkbox", "count", "submit", "selectAll", "bar", "scopeInput", "scopeOffer", "scopeActive"]
  // What is being picked — "form" (default), "site", … — and the action
  // performed on it ("Add", "Remove", …). Drives the live count/submit
  // labels so the controller works for any multi-select list.
  static values = {
    noun: { type: String, default: "form" },
    // Explicit plural ("person" -> "people"); falls back to noun + "s".
    nounPlural: { type: String, default: "" },
    verb: { type: String, default: "Add" },
    // Select-all-across-pages (TASK-252.4, decision-45's one small
    // extension): the true count of the whole filtered relation, which the
    // DOM's own checkboxes can't tell us on a paginated list — only the
    // server, at render time, knows it.
    allInScopeCount: { type: Number, default: 0 }
  }

  connect() {
    this.update()
  }

  // Select-all checkbox: toggles every enabled, VISIBLE checkbox — rows
  // hidden by an active list-search filter are left alone.
  toggleAll(event) {
    const checked = event.target.checked
    this.checkboxTargets.forEach((checkbox) => {
      if (!checkbox.disabled && checkbox.offsetParent !== null) checkbox.checked = checked
    })
    this.update()
  }

  // "Clear" in the selection bar. Unlike toggleAll this ignores visibility:
  // the bar's count includes rows an active search has hidden, so clearing
  // has to reach them too or the count would not fall to zero. Also drops
  // out of "all in scope" mode, if it was on.
  clear() {
    this.checkboxTargets.forEach((checkbox) => {
      if (!checkbox.disabled) checkbox.checked = false
    })
    this.clearScope()
    this.update()
  }

  // The header's "Select all N that <filter>" link (TASK-252.4): ticks this
  // page's own boxes (so the count looks right while JS is in control) and
  // flags the hidden `select_scope` field so the server applies the verb to
  // the whole filtered relation instead of just `row_ids[]`.
  selectAllInScope(event) {
    event.preventDefault()
    this.checkboxTargets.forEach((checkbox) => {
      if (!checkbox.disabled && checkbox.offsetParent !== null) checkbox.checked = true
    })
    if (this.hasScopeInputTarget) this.scopeInputTarget.value = "filter"
    this.scopeOfferTargets.forEach((el) => (el.hidden = true))
    this.scopeActiveTargets.forEach((el) => (el.hidden = false))
    this.update()
  }

  clearScope() {
    if (this.hasScopeInputTarget) this.scopeInputTarget.value = ""
    this.scopeOfferTargets.forEach((el) => (el.hidden = false))
    this.scopeActiveTargets.forEach((el) => (el.hidden = true))
  }

  inScope() {
    return this.hasScopeInputTarget && this.scopeInputTarget.value === "filter"
  }

  update() {
    const enabled = this.checkboxTargets.filter((c) => !c.disabled)
    const selected = enabled.filter((c) => c.checked).length
    const plural = this.nounPluralValue || `${this.nounValue}s`
    const allInScope = this.inScope()

    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = enabled.length > 0 && selected === enabled.length
      this.selectAllTarget.indeterminate = selected > 0 && selected < enabled.length
    }

    this.countTargets.forEach((target) => {
      target.textContent = allInScope
        ? `All ${this.allInScopeCountValue} ${this.allInScopeCountValue === 1 ? this.nounValue : plural} selected`
        : selected === 0
          ? `No ${plural} selected`
          : `${selected} ${selected === 1 ? this.nounValue : plural} selected`
    })

    // The contextual selection bar exists only while something is selected —
    // no reserved empty space at zero (decision-45). The `hidden` ATTRIBUTE,
    // not a class, so the server can render it hidden without knowing
    // Tailwind's utility names.
    this.barTargets.forEach((bar) => {
      bar.hidden = selected === 0 && !allInScope
    })

    this.submitTargets.forEach((target) => {
      // Disabled state only — the label stays as authored. Rewriting it with
      // the count resized the button and shifted its neighbours on every
      // toggle (TASK-81.26 polish); the count span already says the number.
      target.disabled = selected === 0 && !allInScope
    })

    // Select-all, Clear and "select all in scope" tick boxes programmatically,
    // which fires no native `change` on the rows — so a sibling controller
    // listening to row `change` (the Due page's bulk buttons) never sees it.
    // Announce the settled state for those to follow.
    this.dispatch("updated", { bubbles: true })
  }
}
